import { useState } from "react";
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";

// Hover/focus tooltip with collision-aware positioning (@floating-ui). Wraps its child in an
// inline-flex span that serves as the reference element. Use for icon buttons where a bare
// title= is too sparse.
export function Tooltip({
  label,
  placement = "top",
  children,
}: {
  label: string;
  placement?: "top" | "bottom" | "left" | "right";
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Controlled elements (state setters, not a refs object) keep this clear of the
  // react-hooks "no refs during render" rule.
  const [reference, setReference] = useState<HTMLElement | null>(null);
  const [floating, setFloating] = useState<HTMLElement | null>(null);
  const { floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 6 })],
    whileElementsMounted: autoUpdate,
    elements: { reference, floating },
  });
  const hover = useHover(context, { delay: { open: 350, close: 0 }, move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (!label) return <>{children}</>;

  return (
    <>
      <span ref={setReference} {...getReferenceProps()} className="inline-flex">
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[120] max-w-xs rounded border border-border bg-panel2 px-2 py-1 text-[11px] text-fg shadow-xl"
          >
            {label}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
